import React, { FC, Fragment, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import OrganizationPicker from 'components/orders/organization-picker.component';
import { formatDateTime, restorationTypeLabel, statusLabel } from 'components/orders/order-format';
import { adminOrdersTemplate } from 'apollo/templates/orders';
import { AdminOrderFilters, AdminOrderOrganization, AdminOrderPage } from 'models/admin-order';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const STATUSES = ['sent_by_customer', 'accepted_by_lab', 'sent_by_lab', 'order_approved'];

/** Filters live in the URL so a filtered list can be shared and linked to. */
const useOrderListParams = () => {
    const [params, setParams] = useSearchParams();
    const organization = (key: string): AdminOrderOrganization | null =>
        params.get(key) ? { _id: params.get(key) as string, name: params.get(`${key}Name`) ?? undefined } : null;

    const update = (changes: Record<string, string | null | undefined>, resetPage = true) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
        if (resetPage) next.delete('page');
        setParams(next, { replace: true });
    };

    return {
        search: params.get('search') ?? '',
        customer: organization('customer'),
        supplier: organization('supplier'),
        status: params.get('status') ?? '',
        includeDrafts: params.get('drafts') === '1',
        page: Number(params.get('page') ?? 0),
        pageSize: Number(params.get('size') ?? PAGE_SIZE_OPTIONS[0]),
        update,
    };
};

const Orders: FC = () => {
    const { t: translate } = useTranslation(['super-admin', 'orders']);
    const navigate = useNavigate();
    const { search, customer, supplier, status, includeDrafts, page, pageSize, update } = useOrderListParams();
    const [searchInput, setSearchInput] = useState(search);
    const [debouncedSetSearch] = useState(() =>
        debounce((text: string, apply: typeof update) => apply({ search: text.trim() || null }), 400),
    );

    useEffect(() => {
        if (searchInput !== search) debouncedSetSearch(searchInput, update);
    }, [searchInput]);

    const filters: AdminOrderFilters = {
        search: search || undefined,
        customerId: customer?._id,
        supplierId: supplier?._id,
        status: status || undefined,
        includeDrafts,
    };

    const { data, loading, error } = useQuery<{ adminOrders: AdminOrderPage }>(adminOrdersTemplate, {
        variables: { filters, limit: pageSize, offset: page * pageSize },
        fetchPolicy: 'network-only',
    });
    const result = data?.adminOrders;

    const setOrganization = (key: 'customer' | 'supplier', organization: AdminOrderOrganization | null) =>
        update({ [key]: organization?._id, [`${key}Name`]: organization?.name });

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('super-admin:MENU_ORDERS')} />
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={translate('super-admin:ORDER_SEARCH')}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        data-test="orders.search"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <OrganizationPicker
                        label={translate('super-admin:ORDER_CUSTOMER')}
                        value={customer}
                        onChange={(organization) => setOrganization('customer', organization)}
                        dataTest="orders.customer"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <OrganizationPicker
                        label={translate('super-admin:ORDER_SUPPLIER')}
                        value={supplier}
                        type="lab"
                        onChange={(organization) => setOrganization('supplier', organization)}
                        dataTest="orders.supplier"
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{translate('super-admin:ORDER_STATUS')}</InputLabel>
                        <Select
                            variant="outlined"
                            label={translate('super-admin:ORDER_STATUS')}
                            value={status}
                            onChange={(event) => update({ status: event.target.value || null })}
                        >
                            <MenuItem value="">{translate('super-admin:ALL')}</MenuItem>
                            {STATUSES.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {statusLabel(translate, value)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeDrafts}
                                onChange={(event) => update({ drafts: event.target.checked ? '1' : null })}
                            />
                        }
                        label={translate('super-admin:ORDER_INCLUDE_DRAFTS')}
                    />
                </Grid>
            </Grid>

            {error ? <Alert severity="error">{error.message}</Alert> : undefined}
            {result?.searchTruncated ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('super-admin:ORDER_SEARCH_TRUNCATED')}
                </Alert>
            ) : undefined}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('super-admin:ORDER_ID')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_CREATED')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_CUSTOMER')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_SUPPLIER')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_PATIENT')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_CONSTRUCTIONS')}</TableCell>
                            <TableCell>{translate('super-admin:ORDER_STATUS')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Loader />
                                </TableCell>
                            </TableRow>
                        ) : undefined}
                        {!loading && result && result.items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('super-admin:ORDER_NO_RESULTS')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : undefined}
                        {!loading &&
                            result?.items.map((order) => (
                                <TableRow
                                    key={order._id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/orders/${order._id}`)}
                                    data-test="orders.row"
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {order.orderId ?? '–'}
                                        </Typography>
                                        {order.internalOrderId ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {order.internalOrderId}
                                            </Typography>
                                        ) : undefined}
                                    </TableCell>
                                    <TableCell>
                                        {formatDateTime(order.createdOn)}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {order.createdBy?.fullName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{order.customer?.name ?? '–'}</TableCell>
                                    <TableCell>{order.supplier?.name ?? '–'}</TableCell>
                                    <TableCell>{order.patientName ?? '–'}</TableCell>
                                    <TableCell>
                                        {order.restorationTypes
                                            .map((type) => restorationTypeLabel(translate, type))
                                            .join(', ') || '–'}
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={statusLabel(translate, order.status, order.rejectStatus)}
                                            />
                                            {order.isDraft ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={translate('super-admin:ORDER_DRAFT')}
                                                />
                                            ) : undefined}
                                            {order.isPaused ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={translate('super-admin:ORDER_PAUSED')}
                                                />
                                            ) : undefined}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {result ? (
                <TablePagination
                    component="div"
                    count={result.total}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={(_, nextPage) => update({ page: String(nextPage) }, false)}
                    onRowsPerPageChange={(event) => update({ size: event.target.value })}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${translate('super-admin:OF')} ${result.totalIsEstimate ? '~' : ''}${count}`
                    }
                />
            ) : undefined}
        </Fragment>
    );
};

export default Orders;
